import loadVimeoAPI from "./load-vimeo-api.js"
import loadYouTubeAPI from "./load-youtube-api.js"
import {
  addPlayerToTrackingArr,
  pausePlayingVideos,
  onYouTubePlayerStateChange,
  clickPosterToPlayVimeo,
  clickPosterToPlayYouTube,
} from "./video-player-state.js"

/**
 * Create a YouTube video player directly from the API
 * @param {HTMLElement} video video player div that will be swapped out with iframe
 * @param {boolean} hadPoster dictate the presence of poster image over video
 */
const initYouTubeVideo = (video, hadPoster) => {
  const videoId = video.dataset.videoId

  let autoplay = false
  if (hadPoster) {
    autoplay = true
  }

  loadYouTubeAPI().then(() => {
    const player = new window.YT.Player(video, {
      videoId,
      playerVars: {
        enablejsapi: 1,
        cc_load_policy: 0,
        controls: 1,
        iv_load_policy: 3,
        playsinline: 1,
        rel: 0,
        playlist: videoId,
        // eslint-disable-next-line object-shorthand
        autoplay: autoplay,
      },
      events: {
        onReady: () => {
          player.getIframe().tabIndex = "0"
          if (hadPoster) {
            clickPosterToPlayYouTube(player)
          } else {
            player.unMute()
          }

          addPlayerToTrackingArr("youtube", player)
        },
        onStateChange: onYouTubePlayerStateChange,
      },
    })
  })
}

/**
 * Update an existing YouTube iframe to allow for API events and actions
 * @param {HTMLIFrameElement} iframe existing iframe, not originally rendered through the API
 * @param {boolean} hadPoster dictate the presence of poster image over video
 */
const updateIframeYouTubeVideo = (iframe, hadPoster) => {
  loadYouTubeAPI().then(() => {
    const player = new window.YT.Player(iframe, {
      events: {
        onReady: () => {
          player.getIframe().tabIndex = "0"

          if (hadPoster) {
            clickPosterToPlayYouTube(player)
          } else {
            player.unMute()
          }

          addPlayerToTrackingArr("youtube", player)
        },
        onStateChange: onYouTubePlayerStateChange,
      },
    })
  })
}

/**
 * Create a Vimeo video player directly from the API
 * @param {HTMLElement} video video player div that will be swapped out with iframe
 * @param {boolean} hadPoster dictate the presence of poster image over video
 */
const initVimeoVideo = (video, hadPoster) => {
  let autoplay = false
  if (hadPoster) {
    autoplay = true
  }

  loadVimeoAPI().then(() => {
    const player = new window.Vimeo.Player(video, {
      id: video.dataset.videoId,
      // eslint-disable-next-line object-shorthand
      autoplay: autoplay,
    })

    player.element.tabIndex = "0"
    if (hadPoster) {
      clickPosterToPlayVimeo(player)
    }

    pausePlayingVideos()
    addPlayerToTrackingArr("vimeo", player)
  })
}

/**
 * Update an existing Vimeo iframe to allow for API events and actions
 * @param {HTMLIFrameElement} iframe existing iframe, not originally rendered through the API
 * @param {boolean} hadPoster dictate the presence of poster image over video
 */
const updateIframeVimeoVideo = (iframe, hadPoster) => {
  loadVimeoAPI().then(() => {
    const player = new window.Vimeo.Player(iframe, {
      muted: true,
    })

    player.element.tabIndex = "0"
    if (hadPoster) {
      clickPosterToPlayVimeo(player)
    }

    addPlayerToTrackingArr("vimeo", player)
  })
}

export {
  initYouTubeVideo,
  initVimeoVideo,
  updateIframeYouTubeVideo,
  updateIframeVimeoVideo,
}
